import { Howler } from 'howler';
import { clamp } from '../../shared/utils';
import { DroneType, AlgorithmCardType } from '../../shared/types';

/**
 * Procedural audio manager for game sound effects.
 * Uses Web Audio API via Howler.ctx to synthesize sounds -- no audio files needed.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private ambientOsc: OscillatorNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private disposed = false;

  private bgmGain: GainNode | null = null;
  private bgmNodes: { osc: OscillatorNode; gain: GainNode }[] = [];
  private bgmLfo: OscillatorNode | null = null;
  private bgmOstinatoTimer: ReturnType<typeof setInterval> | null = null;
  private bgmOstinatoStep = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatPeriodMs: number | null = null;
  private lastAttackChirpTime = -Infinity;

  constructor() {
    this.initContext();
  }

  private initContext(): void {
    // Howler builds `ctx` lazily on its first setup call; nudge it via
    // Howler.volume() so we don't observe `null` in production.
    if (!Howler.ctx) {
      try { Howler.volume(Howler.volume() ?? 1); } catch { /* ignore */ }
    }
    const ctx = Howler.ctx;
    if (!ctx) return;

    this.ctx = ctx;
    const gain = ctx.createGain();
    gain.gain.value = 0.5;
    gain.connect(ctx.destination);
    this.masterGain = gain;
  }

  private ensureReady(): { ctx: AudioContext; out: GainNode } | null {
    if (!this.ctx) {
      this.initContext();
    }
    if (!this.ctx || !this.masterGain) return null;
    if (this.isCtxSuspended()) return null;
    return { ctx: this.ctx, out: this.masterGain };
  }

  /** Lazily build the BGM bus so it has independent volume from SFX. */
  private ensureBgmGain(ctx: AudioContext, master: GainNode): GainNode {
    if (this.bgmGain) return this.bgmGain;
    const g = ctx.createGain();
    g.gain.value = 0.3; // BGM ~30% of master
    g.connect(master);
    this.bgmGain = g;
    return g;
  }

  /** Schedule a single oscillator tone connected to the master output. */
  private playTone(opts: {
    type: OscillatorType;
    freqStart: number;
    freqEnd: number;
    freqRampEnd: number;
    gainStart: number;
    gainEnd: number;
    gainRampEnd: number;
    startTime?: number;
  }): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = opts.startTime ?? ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freqStart, now);
    osc.frequency.exponentialRampToValueAtTime(opts.freqEnd, now + opts.freqRampEnd);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(opts.gainStart, now);
    gain.gain.exponentialRampToValueAtTime(opts.gainEnd, now + opts.gainRampEnd);

    osc.connect(gain);
    gain.connect(out);
    osc.start(now);
    osc.stop(now + opts.gainRampEnd);
  }

  /** Get or create the cached noise buffer used for explosions. */
  private getNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;

    const bufferSize = Math.floor(ctx.sampleRate * 0.3);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    this.noiseBuffer = buffer;
    return buffer;
  }

  /** Short noise burst with low-frequency decay -- drone destruction. */
  playExplosion(): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = ctx.currentTime;

    // White noise burst
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = this.getNoiseBuffer(ctx);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.6, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    noiseSrc.connect(noiseGain);
    noiseGain.connect(out);
    noiseSrc.start(now);
    noiseSrc.stop(now + 0.3);

    // Low-frequency thump
    this.playTone({
      type: 'sine',
      freqStart: 80,
      freqEnd: 20,
      freqRampEnd: 0.4,
      gainStart: 0.5,
      gainEnd: 0.001,
      gainRampEnd: 0.4,
      startTime: now,
    });
  }

  /** Quick sharp click/impact -- damage dealt. */
  playDamageHit(): void {
    this.playTone({
      type: 'square',
      freqStart: 800,
      freqEnd: 200,
      freqRampEnd: 0.06,
      gainStart: 0.3,
      gainEnd: 0.001,
      gainRampEnd: 0.06,
    });
  }

  /**
   * Crit hit sting layered on top of {@link playDamageHit}: a short metallic
   * ping that reads as "lucky shot" without competing with the base damage
   * tick. Three sequential voices over ~180ms:
   *   1. 120Hz triangle decay  — sub-thump that grounds the hit in the body
   *   2. 800Hz sine ping        — bright peak that draws the ear (the "ping")
   *   3. 240Hz sine sub-rumble  — tail that adds weight and stereo width
   * No throttling: server-side crit chance keeps these rare enough that
   * back-to-back crits should still be audible as separate stings.
   */
  playCrit(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;

    // 1) 120Hz triangle decay — short body thump.
    this.playTone({
      type: 'triangle',
      freqStart: 120,
      freqEnd: 60,
      freqRampEnd: 0.08,
      gainStart: 0.20,
      gainEnd: 0.001,
      gainRampEnd: 0.08,
      startTime: now,
    });

    // 2) 800Hz sine ping — bright midline peak, slight stereo widening
    //    via a very short delay (no panner: keep it cheap).
    this.playTone({
      type: 'sine',
      freqStart: 800,
      freqEnd: 1100,
      freqRampEnd: 0.06,
      gainStart: 0.18,
      gainEnd: 0.001,
      gainRampEnd: 0.10,
      startTime: now + 0.012,
    });

    // 3) 240Hz sine sub-rumble — tail that gives the sting weight.
    this.playTone({
      type: 'sine',
      freqStart: 240,
      freqEnd: 100,
      freqRampEnd: 0.16,
      gainStart: 0.12,
      gainEnd: 0.001,
      gainRampEnd: 0.18,
      startTime: now + 0.02,
    });
  }

  /** Soft ascending chime -- heal applied. */
  playHeal(): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = ctx.currentTime;
    const notes = [523, 659, 784]; // C5, E5, G5

    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = notes[i];

      const gain = ctx.createGain();
      const t = now + i * 0.08;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.2, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

      osc.connect(gain);
      gain.connect(out);
      osc.start(t);
      osc.stop(t + 0.2);
    }
  }

  /**
   * Descending sine triad A5 → E5 → A4 -- Support's Shield Pulse absorbed a
   * hit. Mirrors {@link playHeal}'s envelope shape but inverted in pitch so
   * the player can tell a free save from a normal heal/damage tick by ear.
   */
  playShieldBreak(): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = ctx.currentTime;
    const notes = [880, 659, 440]; // A5, E5, A4

    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = notes[i];

      const gain = ctx.createGain();
      const t = now + i * 0.06;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.10, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);

      osc.connect(gain);
      gain.connect(out);
      osc.start(t);
      osc.stop(t + 0.06);
    }
  }

  /** Bright chirp -- item pickup. */
  playPickup(): void {
    this.playTone({
      type: 'sine',
      freqStart: 600,
      freqEnd: 1200,
      freqRampEnd: 0.1,
      gainStart: 0.25,
      gainEnd: 0.001,
      gainRampEnd: 0.15,
    });
  }

  /**
   * Backward-compat alias for card activation. New code should call one of
   * the tier-specific methods (`playCommonCardSound`, `playRareCardSound`,
   * `playLegendCardSound`) so player feedback matches the card's power.
   * Mid-tier (Rare) is the default — it best matches the original sound's
   * "punchy whoosh" character.
   */
  playCardActivate(): void {
    this.playRareCardSound();
  }

  /**
   * Common-tier card activation: soft sawtooth whoosh 200→800Hz over 300ms.
   * Same envelope as the original `playCardActivate` but at ~60% gain so
   * low-impact cards (Regroup/Scatter/Follow/Orbit/HorizontalOrbit/
   * DiagonalOrbit) feel subdued.
   */
  playCommonCardSound(): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.2);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.06, now + 0.05);
    gain.gain.setValueAtTime(0.06, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    osc.connect(gain);
    gain.connect(out);
    osc.start(now);
    osc.stop(now + 0.3);
  }

  /**
   * Rare-tier card activation: punchy electronic burst — square + saw mix
   * sweeping 200→1600Hz over 280ms, plus a brief noise tail (~30ms) for
   * grit. Used by mid-tier cards (DefensiveWall/RadarRing/FocusFire/RelayNet).
   */
  playRareCardSound(): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = ctx.currentTime;
    const dur = 0.28;

    // Square voice — provides the bright, hard edge.
    const sqOsc = ctx.createOscillator();
    sqOsc.type = 'square';
    sqOsc.frequency.setValueAtTime(200, now);
    sqOsc.frequency.exponentialRampToValueAtTime(1600, now + dur);
    const sqGain = ctx.createGain();
    sqGain.gain.setValueAtTime(0, now);
    sqGain.gain.linearRampToValueAtTime(0.07, now + 0.03);
    sqGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    sqOsc.connect(sqGain);
    sqGain.connect(out);
    sqOsc.start(now);
    sqOsc.stop(now + dur);

    // Sawtooth voice — fattens the body of the burst.
    const sawOsc = ctx.createOscillator();
    sawOsc.type = 'sawtooth';
    sawOsc.frequency.setValueAtTime(200, now);
    sawOsc.frequency.exponentialRampToValueAtTime(1600, now + dur);
    const sawGain = ctx.createGain();
    sawGain.gain.setValueAtTime(0, now);
    sawGain.gain.linearRampToValueAtTime(0.05, now + 0.03);
    sawGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    sawOsc.connect(sawGain);
    sawGain.connect(out);
    sawOsc.start(now);
    sawOsc.stop(now + dur);

    // Brief noise tail for grit.
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = this.getNoiseBuffer(ctx);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.08, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
    noiseSrc.connect(noiseGain);
    noiseGain.connect(out);
    noiseSrc.start(now);
    noiseSrc.stop(now + 0.03);
  }

  /**
   * Legend-tier card activation: dramatic — a sustained 80Hz sine pad
   * (~600ms) underneath a C5+G5+C6 bell triad with bell-like exponential
   * decay. Used by ultimate cards (CageSurround/SyncDive/ConvertShot/
   * ChoreoUltimate).
   */
  playLegendCardSound(): void {
    const ready = this.ensureReady();
    if (!ready) return;

    const { ctx, out } = ready;
    const now = ctx.currentTime;

    // Low rumble pad — sets the "this matters" floor.
    const pad = ctx.createOscillator();
    pad.type = 'sine';
    pad.frequency.value = 80;
    const padGain = ctx.createGain();
    padGain.gain.setValueAtTime(0, now);
    padGain.gain.linearRampToValueAtTime(0.10, now + 0.05);
    padGain.gain.setValueAtTime(0.10, now + 0.4);
    padGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    pad.connect(padGain);
    padGain.connect(out);
    pad.start(now);
    pad.stop(now + 0.62);

    // Bell triad C5 / G5 / C6 — bright stacked fifths/octave.
    const bellFreqs = [523, 784, 1047];
    for (const f of bellFreqs) {
      const bell = ctx.createOscillator();
      bell.type = 'sine';
      bell.frequency.value = f;
      const bg = ctx.createGain();
      bg.gain.setValueAtTime(0, now);
      bg.gain.linearRampToValueAtTime(0.08, now + 0.01);
      bg.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      bell.connect(bg);
      bg.connect(out);
      bell.start(now);
      bell.stop(now + 0.42);
    }
  }

  /**
   * Schedule a single sine/triangle/square/saw note with a clip-safe ADSR.
   * Used as the primitive for per-card synthesis. All times are seconds.
   */
  private playNote(opts: {
    type: OscillatorType;
    freq: number;
    startTime: number;
    attack: number;
    duration: number;
    peakGain: number;
    detune?: number;
    output?: AudioNode;
  }): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const dest = opts.output ?? out;
    const osc = ctx.createOscillator();
    osc.type = opts.type;
    osc.frequency.value = opts.freq;
    if (opts.detune !== undefined) osc.detune.value = opts.detune;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, opts.startTime);
    g.gain.linearRampToValueAtTime(opts.peakGain, opts.startTime + opts.attack);
    g.gain.exponentialRampToValueAtTime(0.001, opts.startTime + opts.duration);
    osc.connect(g);
    g.connect(dest);
    osc.start(opts.startTime);
    osc.stop(opts.startTime + opts.duration + 0.02);
  }

  /**
   * Per-card activation sounds — one method per AlgorithmCardType. Each is
   * unmistakable on a quick listen (different envelope, scale, timbre) but
   * still wears its tier "feel": Commons are punchier and shorter, Legends
   * are richer with sub-bass / chords / reverb tails.
   */

  /** Regroup (Common): bright C-major triad, fast attack — "rally up". */
  playCardActivation_Regroup(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // C5/E5/G5 triad, all start together with ~5ms attack for a snap.
    for (const f of [523.25, 659.25, 783.99]) {
      this.playNote({ type: 'triangle', freq: f, startTime: now, attack: 0.005, duration: 0.22, peakGain: 0.10 });
    }
  }

  /** Scatter (Common): rapid descending arpeggio — "spread out". */
  playCardActivation_Scatter(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // G5 → E5 → C5 → A4, 50ms steps.
    const notes = [783.99, 659.25, 523.25, 440.00];
    for (let i = 0; i < notes.length; i++) {
      this.playNote({ type: 'square', freq: notes[i], startTime: now + i * 0.05, attack: 0.005, duration: 0.10, peakGain: 0.08 });
    }
  }

  /** Follow (Common): short rising sine sweep — "come along". */
  playCardActivation_Follow(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(330, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.20);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.12, now + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    osc.connect(g);
    g.connect(out);
    osc.start(now);
    osc.stop(now + 0.24);
  }

  /** Orbit (Common): circular detuned hum — two slightly-detuned sines. */
  playCardActivation_Orbit(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // 440Hz pair, +/- 12 cents detune for a slow beating "circular" feel.
    this.playNote({ type: 'sine', freq: 440, startTime: now, attack: 0.04, duration: 0.34, peakGain: 0.10, detune: -12 });
    this.playNote({ type: 'sine', freq: 440, startTime: now, attack: 0.04, duration: 0.34, peakGain: 0.10, detune: 12 });
  }

  /** DefensiveWall (Rare): low brass-like sub-bass thunk — heavy and grounded. */
  playCardActivation_DefensiveWall(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    // Sawtooth fundamental swept slightly downward for the "thunk" body.
    const saw = ctx.createOscillator();
    saw.type = 'sawtooth';
    saw.frequency.setValueAtTime(110, now);
    saw.frequency.exponentialRampToValueAtTime(82, now + 0.30);
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0, now);
    sg.gain.linearRampToValueAtTime(0.18, now + 0.02);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
    saw.connect(sg);
    sg.connect(out);
    saw.start(now);
    saw.stop(now + 0.34);
    // Sub-bass sine doubles the fundamental an octave below.
    this.playNote({ type: 'sine', freq: 55, startTime: now, attack: 0.005, duration: 0.34, peakGain: 0.20 });
  }

  /** RadarRing (Rare): high pulsing ping — three sine pulses an octave up. */
  playCardActivation_RadarRing(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // Three short pings at 1760Hz (A6) spaced 90ms apart.
    for (let i = 0; i < 3; i++) {
      this.playNote({ type: 'sine', freq: 1760, startTime: now + i * 0.09, attack: 0.005, duration: 0.07, peakGain: 0.12 });
    }
  }

  /** FocusFire (Rare): sharp metallic click — square + filtered noise. */
  playCardActivation_FocusFire(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    // Square chirp 2200→1100Hz over 30ms — the "click".
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(2200, now);
    osc.frequency.exponentialRampToValueAtTime(1100, now + 0.03);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.18, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    osc.connect(g);
    g.connect(out);
    osc.start(now);
    osc.stop(now + 0.07);
    // Tight noise transient gives it metallic bite.
    const ns = ctx.createBufferSource();
    ns.buffer = this.getNoiseBuffer(ctx);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.10, now);
    ng.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
    ns.connect(ng);
    ng.connect(out);
    ns.start(now);
    ns.stop(now + 0.05);
  }

  /** RelayNet (Rare): warm chord with a delay/reverb tail. */
  playCardActivation_RelayNet(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    // Wet bus with a feedback delay simulates a short reverb tail.
    const wet = ctx.createGain();
    wet.gain.value = 0.35;
    const delay = ctx.createDelay();
    delay.delayTime.value = 0.18;
    const fb = ctx.createGain();
    fb.gain.value = 0.45;
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(wet);
    wet.connect(out);
    // F major triad F4/A4/C5 — feeds both the dry out and the wet bus.
    for (const f of [349.23, 440.00, 523.25]) {
      this.playNote({ type: 'sine', freq: f, startTime: now, attack: 0.02, duration: 0.30, peakGain: 0.09 });
      this.playNote({ type: 'sine', freq: f, startTime: now, attack: 0.02, duration: 0.30, peakGain: 0.09, output: delay });
    }
  }

  /** CageSurround (Legend): A-minor chord with a slow attack — ominous lock-in. */
  playCardActivation_CageSurround(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // A-minor triad A3/C4/E4, slow 80ms attack, long ~600ms tail.
    for (const f of [220.00, 261.63, 329.63]) {
      this.playNote({ type: 'sine', freq: f, startTime: now, attack: 0.08, duration: 0.60, peakGain: 0.10 });
    }
    // Sub-bass octave doubles the root for weight.
    this.playNote({ type: 'sine', freq: 110, startTime: now, attack: 0.10, duration: 0.62, peakGain: 0.12 });
  }

  /** SyncDive (Legend): descending whoosh — sawtooth slide + noise tail. */
  playCardActivation_SyncDive(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    // Sawtooth slides 1500→200Hz over 350ms.
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(1500, now);
    osc.frequency.exponentialRampToValueAtTime(200, now + 0.35);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.16, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.40);
    osc.connect(g);
    g.connect(out);
    osc.start(now);
    osc.stop(now + 0.42);
    // Noise tail mimics air rushing past.
    const ns = ctx.createBufferSource();
    ns.buffer = this.getNoiseBuffer(ctx);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0, now);
    ng.gain.linearRampToValueAtTime(0.10, now + 0.05);
    ng.gain.exponentialRampToValueAtTime(0.001, now + 0.40);
    ns.connect(ng);
    ng.connect(out);
    ns.start(now);
    ns.stop(now + 0.42);
  }

  /** ConvertShot (Legend): glitchy bit-crushed pulse — square stair-step. */
  playCardActivation_ConvertShot(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // Quantized "bit-crushed" feel: 6 square pings at random-ish pitches.
    const pitches = [440, 660, 330, 880, 550, 220];
    for (let i = 0; i < pitches.length; i++) {
      this.playNote({ type: 'square', freq: pitches[i], startTime: now + i * 0.03, attack: 0.001, duration: 0.04, peakGain: 0.10 });
    }
  }

  /** ChoreoUltimate (Legend): full triad fanfare with sub-bass + reverb. */
  playCardActivation_ChoreoUltimate(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    // Wet bus (delay feedback) for a short reverb tail.
    const wet = ctx.createGain();
    wet.gain.value = 0.30;
    const delay = ctx.createDelay();
    delay.delayTime.value = 0.16;
    const fb = ctx.createGain();
    fb.gain.value = 0.40;
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(wet);
    wet.connect(out);
    // C-major fanfare: C5/E5/G5/C6 ascending arpeggio (60ms steps), each
    // also dispatched into the wet bus for the reverb tail.
    const notes = [523.25, 659.25, 783.99, 1046.50];
    for (let i = 0; i < notes.length; i++) {
      const t = now + i * 0.06;
      this.playNote({ type: 'triangle', freq: notes[i], startTime: t, attack: 0.01, duration: 0.45, peakGain: 0.10 });
      this.playNote({ type: 'triangle', freq: notes[i], startTime: t, attack: 0.01, duration: 0.45, peakGain: 0.10, output: delay });
    }
    // Sub-bass anchor — held C2 sine.
    this.playNote({ type: 'sine', freq: 65.41, startTime: now, attack: 0.03, duration: 0.65, peakGain: 0.18 });
  }

  /** HorizontalOrbit (Common): soft circular sweep, brighter than Orbit. */
  playCardActivation_HorizontalOrbit(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // 660Hz detuned pair (a fifth above Orbit's 440Hz) for a brighter circle.
    this.playNote({ type: 'sine', freq: 660, startTime: now, attack: 0.04, duration: 0.30, peakGain: 0.09, detune: -10 });
    this.playNote({ type: 'sine', freq: 660, startTime: now, attack: 0.04, duration: 0.30, peakGain: 0.09, detune: 10 });
  }

  /** DiagonalOrbit (Common): rising-tilted sweep variant — pitch climbs. */
  playCardActivation_DiagonalOrbit(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    // Two detuned sines that both ramp 440→880Hz over 280ms — the "tilt".
    for (const detune of [-10, 10]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.28);
      osc.detune.value = detune;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.09, now + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.30);
      osc.connect(g);
      g.connect(out);
      osc.start(now);
      osc.stop(now + 0.32);
    }
  }

  /** Subtle looping background hum -- call once on game start. */
  playAmbient(): void {
    const ready = this.ensureReady();
    if (!ready || this.ambientOsc) return;

    const { ctx, out } = ready;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 55;

    const gain = ctx.createGain();
    gain.gain.value = 0.04;

    osc.connect(gain);
    gain.connect(out);
    osc.start();
    this.ambientOsc = osc;
  }

  /**
   * Procedural ambient music — a slow A-minor pad plus a 4-step ostinato.
   * ~70 BPM (one ostinato note every 857ms). Subtle LFO modulates the pad
   * over a ~40s cycle. Idempotent — calling while already playing is a no-op.
   */
  playBgmLoop(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    if (this.bgmOstinatoTimer !== null) return; // already running

    const { ctx, out } = ready;
    const bgmOut = this.ensureBgmGain(ctx, out);

    // Three-voice minor pad (A2, E3, C4 -- A minor triad)
    const padFreqs = [110, 164.81, 261.63];
    for (const f of padFreqs) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = 0.18;
      osc.connect(g);
      g.connect(bgmOut);
      osc.start();
      this.bgmNodes.push({ osc, gain: g });
    }

    // Slow LFO on the pad's master gain for atmospheric breathing.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 1 / 40; // 40s period
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.05;
    lfo.connect(lfoGain);
    lfoGain.connect(bgmOut.gain);
    lfo.start();
    this.bgmLfo = lfo;

    // Ostinato — 4-step minor figure repeating every 4 * 857ms.
    const ostinatoNotes = [220, 261.63, 329.63, 261.63]; // A3, C4, E4, C4
    const stepMs = (60 / 70) * 1000; // 70 BPM quarter note
    this.bgmOstinatoStep = 0;
    this.bgmOstinatoTimer = setInterval(() => {
      const note = ostinatoNotes[this.bgmOstinatoStep % ostinatoNotes.length];
      this.bgmOstinatoStep++;
      this.playBgmOstinatoNote(note);
    }, stepMs);
  }

  private playBgmOstinatoNote(freq: number): void {
    if (!this.ctx || !this.bgmGain) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.12, now + 0.05);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc.connect(g);
    g.connect(this.bgmGain);
    osc.start(now);
    osc.stop(now + 0.65);
  }

  /** Tear down the BGM voices. Idempotent. */
  stopBgmLoop(): void {
    if (this.bgmOstinatoTimer !== null) {
      clearInterval(this.bgmOstinatoTimer);
      this.bgmOstinatoTimer = null;
    }
    for (const node of this.bgmNodes) {
      try { node.osc.stop(); } catch { /* already stopped */ }
      node.osc.disconnect();
      node.gain.disconnect();
    }
    this.bgmNodes = [];
    if (this.bgmLfo) {
      try { this.bgmLfo.stop(); } catch { /* already stopped */ }
      this.bgmLfo.disconnect();
      this.bgmLfo = null;
    }
  }

  /** UI tick — bright `false` for press, softer `true` for hover. */
  playUiClick(soft: boolean = false): void {
    this.playTone({
      type: soft ? 'sine' : 'square',
      freqStart: soft ? 1400 : 2200,
      freqEnd: soft ? 1100 : 1500,
      freqRampEnd: 0.04,
      gainStart: soft ? 0.08 : 0.15,
      gainEnd: 0.001,
      gainRampEnd: 0.05,
    });
  }

  /** Short "pew" — drone weapon fire. Throttled to 3/sec. */
  playAttackChirp(): void {
    // `ensureReady` returns null when the context is suspended, so the
    // throttle clock isn't advanced — first chirp after the user gesture
    // still fires.
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx } = ready;
    const now = ctx.currentTime;
    if (now - this.lastAttackChirpTime < 1 / 3) return; // 3 chirps/sec cap
    this.lastAttackChirpTime = now;

    this.playTone({
      type: 'square',
      freqStart: 1200,
      freqEnd: 280,
      freqRampEnd: 0.1,
      gainStart: 0.16, // 40% of typical SFX (0.4 -> attenuated)
      gainEnd: 0.001,
      gainRampEnd: 0.12,
      startTime: now,
    });
  }

  private static readonly HEARTBEAT_PERIOD_EPSILON_MS = 1;

  /**
   * Slow heartbeat. BPM ramps from 60 (at hpFraction = 0.25) to 120
   * (at hpFraction = 0). Calling with `active = false` stops it.
   *
   * Idempotent on the active path: when already running, repeated calls at
   * the same tempo (within {@link HEARTBEAT_PERIOD_EPSILON_MS}) are no-ops
   * — they neither re-thump nor restart the interval. Only true tempo
   * changes (or the inactive→active transition) reset the timer. This
   * prevents a state observer that ticks at 20-60Hz from stacking
   * overlapping `lub-dub` thumps.
   */
  setLowHpHeartbeat(active: boolean, hpFraction: number = 0.25): void {
    if (!active) {
      if (this.heartbeatTimer !== null) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      this.heartbeatPeriodMs = null;
      return;
    }

    // BPM 60 at 0.25 fraction, 120 at 0.0 fraction.
    const t = clamp(1 - hpFraction / 0.25, 0, 1);
    const bpm = 60 + 60 * t;
    const periodMs = (60 / bpm) * 1000;

    if (
      this.heartbeatTimer !== null &&
      this.heartbeatPeriodMs !== null &&
      Math.abs(periodMs - this.heartbeatPeriodMs) < AudioManager.HEARTBEAT_PERIOD_EPSILON_MS
    ) {
      return;
    }

    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
    }
    this.heartbeatPeriodMs = periodMs;
    this.playHeartbeatThump();
    this.heartbeatTimer = setInterval(
      () => this.playHeartbeatThump(),
      periodMs,
    );
  }

  private playHeartbeatThump(): void {
    // Two thumps in quick succession (lub-dub).
    this.playTone({
      type: 'sine',
      freqStart: 80,
      freqEnd: 40,
      freqRampEnd: 0.18,
      gainStart: 0.25,
      gainEnd: 0.001,
      gainRampEnd: 0.2,
    });
    const ready = this.ensureReady();
    if (!ready) return;
    const startTime = ready.ctx.currentTime + 0.18;
    this.playTone({
      type: 'sine',
      freqStart: 70,
      freqEnd: 35,
      freqRampEnd: 0.16,
      gainStart: 0.2,
      gainEnd: 0.001,
      gainRampEnd: 0.18,
      startTime,
    });
  }

  /** True when the AudioContext is suspended/closed (browser autoplay policy). */
  private isCtxSuspended(): boolean {
    if (!this.ctx) return true;
    const s = (this.ctx as AudioContext).state;
    return s === 'suspended' || s === 'closed';
  }

  /**
   * Resume the AudioContext. Chrome/Safari start it `'suspended'` until a
   * user gesture; call this from the first `mousedown`/`keydown`. Idempotent.
   */
  resumeAudioContext(): Promise<void> | void {
    if (!this.ctx) this.initContext();
    if (!this.ctx || !this.isCtxSuspended()) return;
    const ctx = this.ctx as AudioContext & { resume?: () => Promise<void> };
    if (typeof ctx.resume !== 'function') return;
    try {
      return ctx.resume();
    } catch {
      // Some browsers throw synchronously when called outside a gesture.
    }
  }

  /**
   * Shield-break shatter: high-pitch metallic sweep 1200→600Hz over 180ms,
   * routed through a tracking band-pass for a "ringing crystal" character.
   * Layered over {@link playShieldBreak} so an absorbed hit reads as a
   * distinct, instantly-recognizable event vs. a normal damage tick.
   */
  playShieldBreakShatter(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    const dur = 0.18;

    // Square gives the band-pass enough harmonics to ring; sine would not.
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(1200, now);
    osc.frequency.exponentialRampToValueAtTime(600, now + dur);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 8;
    filter.frequency.setValueAtTime(1200, now);
    filter.frequency.exponentialRampToValueAtTime(600, now + dur);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.16, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(out);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }

  /**
   * Card-ability "ready" chirp: ascending G5→C6 diad at a low peak gain so
   * it doesn't compete with BGM. Fires on the cooldownRemaining rising-edge
   * (caller does the change-detection).
   */
  playCardReadyChirp(): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    // Peak 0.05 is below SFX (~0.16) and BGM ostinato (~0.12) — subtle hint,
    // not an alert.
    const notes = [783.99, 1046.50]; // G5, C6
    for (let i = 0; i < notes.length; i++) {
      this.playNote({
        type: 'sine',
        freq: notes[i],
        startTime: now + i * 0.06,
        attack: 0.01,
        duration: 0.10,
        peakGain: 0.05,
      });
    }
  }

  /**
   * Distinct kill-streak tier sound — note count varies by level so the ear
   * can pick the streak depth without seeing the toast:
   *   level 1 (double):  two-note octave   C5 + C6
   *   level 2 (triple):  three-note arpeggio C5/E5/G5
   *   level 3+ (spree):  four-note sweep C5/E5/G5/C6 plus 65Hz sub-rumble tail
   * Layered ADDITIVELY on top of {@link playStreakSound} (the escalating
   * diad). Defaults to level 1 if the input is missing/invalid.
   */
  playKillStreak(level?: number): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const now = ready.ctx.currentTime;
    const lvl = typeof level === 'number' && level >= 1 ? Math.min(level, 3) : 1;

    const NOTES_BY_LEVEL: Record<1 | 2 | 3, number[]> = {
      1: [523.25, 1046.50],                  // C5 + C6
      2: [523.25, 659.25, 783.99],           // C5/E5/G5
      3: [523.25, 659.25, 783.99, 1046.50],  // C5/E5/G5/C6
    };
    const notes = NOTES_BY_LEVEL[lvl as 1 | 2 | 3];

    for (let i = 0; i < notes.length; i++) {
      this.playNote({
        type: 'triangle',
        freq: notes[i],
        startTime: now + i * 0.05,
        attack: 0.01,
        duration: 0.18,
        peakGain: 0.09,
      });
    }

    if (lvl >= 3) {
      // Sub-rumble tail anchors the spree without competing with the bright
      // triangle melody on top.
      this.playNote({
        type: 'sine',
        freq: 65,
        startTime: now,
        attack: 0.04,
        duration: 0.55,
        peakGain: 0.12,
      });
    }
  }

  /**
   * Escalating two-tone chime for multi-kills. Each level lifts the diad by a
   * perfect fourth so the listener hears a rising musical sequence across
   * back-to-back streak announcements.
   *   level 1 (DOUBLE): C5 + F5
   *   level 2 (TRIPLE): F5 + Bb5
   *   level 3 (QUAD):   Bb5 + Eb6
   *   level 4 (PENTA):  Eb6 + Ab6 plus an octave-bass thump for impact
   * ~280ms ADSR sine voice at moderate gain.
   */
  playStreakSound(level: 1 | 2 | 3 | 4): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;

    // Diad pairs indexed by level - 1.
    const diads: Array<[number, number]> = [
      [523.25, 698.46],   // DOUBLE: C5, F5
      [698.46, 932.33],   // TRIPLE: F5, Bb5
      [932.33, 1244.51],  // QUAD:   Bb5, Eb6
      [1244.51, 1661.22], // PENTA:  Eb6, Ab6
    ];
    const [low, high] = diads[level - 1];

    this.playStreakVoice(ctx, out, low, now, 0.12);
    this.playStreakVoice(ctx, out, high, now, 0.10);

    if (level === 4) {
      // Sub-bass thump for the pentakill landing — separate sine voice an
      // octave below the diad's lower note.
      this.playStreakVoice(ctx, out, 165, now, 0.18, 0.45);
    }
  }

  private playStreakVoice(
    ctx: AudioContext,
    out: AudioNode,
    freq: number,
    startTime: number,
    peakGain: number,
    durationSec: number = 0.28,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    // ADSR: attack 30ms → small sustain → exponential release.
    g.gain.setValueAtTime(0, startTime);
    g.gain.linearRampToValueAtTime(peakGain, startTime + 0.03);
    g.gain.setValueAtTime(peakGain, startTime + 0.08);
    g.gain.exponentialRampToValueAtTime(0.001, startTime + durationSec);
    osc.connect(g);
    g.connect(out);
    osc.start(startTime);
    osc.stop(startTime + durationSec + 0.02);
  }

  /** Ascending C-major arpeggio C4-E4-G4-C5 — local player victory. */
  playVictoryFanfare(): void {
    this.playArpeggio([261.63, 329.63, 392.0, 523.25], 0.15, 'triangle');
  }

  /** Descending diminished phrase G4-Db4-Ab3 — local player defeat. */
  playDefeatSting(): void {
    this.playArpeggio([392.0, 277.18, 207.65], 0.25, 'sawtooth');
  }

  private playArpeggio(notes: number[], stepSec: number, type: OscillatorType): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;
    for (let i = 0; i < notes.length; i++) {
      const t = now + i * stepSec;
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = notes[i];
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.28, t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, t + stepSec + 0.2);
      osc.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + stepSec + 0.25);
    }
  }

  /**
   * Throttle map for per-attacker impact sounds. Keyed by
   * `<methodName>:<attackerId>` so each method tracks its own per-source
   * cap independently. Cap is 4 plays/sec/source (250ms gap) — without
   * this, a fast-firing Striker swarm can stack identical clicks every
   * tick and turn the mix into noise. Calls beyond the cap are dropped
   * silently (no console spam).
   */
  private impactLastPlayedMs: Map<string, number> = new Map();
  private static readonly IMPACT_THROTTLE_MS = 250; // 4 plays/sec/source
  /**
   * Stale-entry pruning state. Entries older than IMPACT_PRUNE_MAX_AGE_MS are
   * dropped on every IMPACT_PRUNE_INTERVAL_MS — keeps the map bounded to
   * "attackers active in the last 5s × method types" instead of growing
   * unboundedly across a long match (every distinct attackerId × method tuple
   * leaves a permanent entry otherwise).
   */
  private impactLastPruneMs = 0;
  private static readonly IMPACT_PRUNE_INTERVAL_MS = 5000;
  private static readonly IMPACT_PRUNE_MAX_AGE_MS = 5000;

  /**
   * Returns true if the call should proceed (and updates the timestamp);
   * false if the per-source rate cap has been hit and the call should
   * drop. `attackerId` may be undefined — undefined sources share a
   * single throttle bucket (rare path; defensive).
   */
  private throttleImpact(method: string, attackerId: string | undefined): boolean {
    const key = `${method}:${attackerId ?? '_anon'}`;
    const ready = this.ensureReady();
    // When the AudioContext isn't ready, don't burn the throttle — let the
    // play* method itself early-return. We use Date.now() as a fallback
    // monotonic clock so tests can advance time via ctx.currentTime OR
    // via real wall-clock without coupling to either.
    const nowMs = ready ? ready.ctx.currentTime * 1000 : Date.now();
    // Opportunistic prune of stale entries — bounded cost (one walk per
    // 5s window) keeps the map size proportional to recent attackers, not
    // every attacker that ever fired.
    if (nowMs - this.impactLastPruneMs > AudioManager.IMPACT_PRUNE_INTERVAL_MS) {
      this.impactLastPruneMs = nowMs;
      const cutoff = nowMs - AudioManager.IMPACT_PRUNE_MAX_AGE_MS;
      for (const [k, v] of this.impactLastPlayedMs) {
        if (v < cutoff) this.impactLastPlayedMs.delete(k);
      }
    }
    const last = this.impactLastPlayedMs.get(key);
    if (last !== undefined && nowMs - last < AudioManager.IMPACT_THROTTLE_MS) {
      return false;
    }
    this.impactLastPlayedMs.set(key, nowMs);
    return true;
  }

  /**
   * Per-attacker impact dispatcher. Routes to the type-specific synthesis
   * (sniper crack, striker crackle, healer chime, interceptor whoosh,
   * support ring-mod, guardian thud) gated by the per-method × attacker
   * throttle. Public per-type methods below are thin shims that delegate
   * here so the throttle key + ensureReady() handshake live in one place.
   */
  private playImpact(droneType: DroneType | number, attackerId?: string): void {
    if (!this.throttleImpact(`type:${droneType}`, attackerId)) return;
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;

    switch (droneType as DroneType) {
      case DroneType.Sniper: {
        // Sharp crack: square 1500→400Hz over 80ms + brief noise transient.
        this.playTone({
          type: 'square',
          freqStart: 1500, freqEnd: 400, freqRampEnd: 0.08,
          gainStart: 0.20, gainEnd: 0.001, gainRampEnd: 0.08,
          startTime: now,
        });
        const ns = ctx.createBufferSource();
        ns.buffer = this.getNoiseBuffer(ctx);
        const ng = ctx.createGain();
        ng.gain.setValueAtTime(0.10, now);
        ng.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
        ns.connect(ng);
        ng.connect(out);
        ns.start(now);
        ns.stop(now + 0.04);
        return;
      }
      case DroneType.Striker: {
        // Crackle: 3 rapid 800→200Hz micro-bursts staggered ~25ms apart.
        for (let i = 0; i < 3; i++) {
          this.playTone({
            type: 'square',
            freqStart: 800, freqEnd: 200, freqRampEnd: 0.03,
            gainStart: 0.14, gainEnd: 0.001, gainRampEnd: 0.03,
            startTime: now + i * 0.025,
          });
        }
        return;
      }
      case DroneType.Healer: {
        // Soft chime: sine E5+G5 dyad (minor third), gentle attack/release.
        for (const f of [659.25, 783.99]) {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = f;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(0.10, now + 0.04);
          g.gain.exponentialRampToValueAtTime(0.001, now + 0.20);
          osc.connect(g);
          g.connect(out);
          osc.start(now);
          osc.stop(now + 0.22);
        }
        return;
      }
      case DroneType.Interceptor: {
        // Zip whoosh: bandpass-filtered noise sweep 2200→800Hz over 120ms.
        const dur = 0.12;
        const ns = ctx.createBufferSource();
        ns.buffer = this.getNoiseBuffer(ctx);
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.Q.value = 4;
        filter.frequency.setValueAtTime(2200, now);
        filter.frequency.exponentialRampToValueAtTime(800, now + dur);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.18, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, now + dur);
        ns.connect(filter);
        filter.connect(g);
        g.connect(out);
        ns.start(now);
        ns.stop(now + dur + 0.02);
        return;
      }
      case DroneType.Support: {
        // Metallic pulse: 440Hz triangle + 220Hz sine ring-mod partner; 100ms.
        const dur = 0.10;
        const carrier = ctx.createOscillator();
        carrier.type = 'triangle';
        carrier.frequency.value = 440;
        const mod = ctx.createOscillator();
        mod.type = 'sine';
        mod.frequency.value = 220;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.14, now + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, now + dur);
        carrier.connect(g);
        mod.connect(g);
        g.connect(out);
        carrier.start(now);
        mod.start(now);
        carrier.stop(now + dur + 0.02);
        mod.stop(now + dur + 0.02);
        return;
      }
      case DroneType.Guardian: {
        // Heavy thud: 110→55Hz sine + low-passed noise body; 150ms.
        const dur = 0.15;
        this.playTone({
          type: 'sine',
          freqStart: 110, freqEnd: 55, freqRampEnd: dur,
          gainStart: 0.24, gainEnd: 0.001, gainRampEnd: dur,
          startTime: now,
        });
        const ns = ctx.createBufferSource();
        ns.buffer = this.getNoiseBuffer(ctx);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 200;
        const ng = ctx.createGain();
        ng.gain.setValueAtTime(0.16, now);
        ng.gain.exponentialRampToValueAtTime(0.001, now + dur);
        ns.connect(lp);
        lp.connect(ng);
        ng.connect(out);
        ns.start(now);
        ns.stop(now + dur + 0.02);
        return;
      }
      default:
        // Unknown type — fall back to the generic damage hit. This is the
        // same path `playImpactByDroneType` used to take pre-consolidation.
        this.playDamageHit();
    }
  }

  // Public per-type shims — preserve the original API so existing call sites
  // and the per-method test suite (AudioManager.impact.test.ts) keep working.
  playSniperImpact(attackerId?: string): void { this.playImpact(DroneType.Sniper, attackerId); }
  playStrikerImpact(attackerId?: string): void { this.playImpact(DroneType.Striker, attackerId); }
  playHealerImpact(attackerId?: string): void { this.playImpact(DroneType.Healer, attackerId); }
  playInterceptorImpact(attackerId?: string): void { this.playImpact(DroneType.Interceptor, attackerId); }
  playSupportImpact(attackerId?: string): void { this.playImpact(DroneType.Support, attackerId); }
  playGuardianImpact(attackerId?: string): void { this.playImpact(DroneType.Guardian, attackerId); }

  /**
   * Dispatch a per-attacker impact sound by `DroneType`. Falls back to the
   * generic {@link playDamageHit} when the type isn't recognized so the
   * caller never has to special-case "unknown attacker".
   */
  playImpactByDroneType(droneType: DroneType | number, attackerId?: string): void {
    this.playImpact(droneType, attackerId);
  }

  /**
   * Dispatch an ability-specific impact sound. Most cards have no impact
   * (they're movement / formation), so the default is a no-op. Three
   * Legend-tier cards land impacts that the player should hear distinctly:
   *   CageSurround → metallic lock-in chime
   *   SyncDive     → descending whoosh (mirrors the activation)
   *   ConvertShot  → bit-crush blip
   */
  playAbilityImpact(cardType: AlgorithmCardType): void {
    const ready = this.ensureReady();
    if (!ready) return;
    const { ctx, out } = ready;
    const now = ctx.currentTime;

    switch (cardType) {
      case AlgorithmCardType.CageSurround: {
        // Metallic lock-in: filtered square at 1100Hz with a Q-bell so it
        // rings briefly — like a cage door slamming shut.
        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.value = 1100;
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.Q.value = 10;
        filter.frequency.value = 1100;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.14, now + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        osc.connect(filter);
        filter.connect(g);
        g.connect(out);
        osc.start(now);
        osc.stop(now + 0.20);
        break;
      }
      case AlgorithmCardType.SyncDive: {
        // Descending whoosh — sawtooth slide 1400→180Hz, 200ms.
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(1400, now);
        osc.frequency.exponentialRampToValueAtTime(180, now + 0.20);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.16, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
        osc.connect(g);
        g.connect(out);
        osc.start(now);
        osc.stop(now + 0.24);
        break;
      }
      case AlgorithmCardType.ConvertShot: {
        // Bit-crush blip — three quantized square pulses (sounds digital).
        const pitches = [880, 660, 440];
        for (let i = 0; i < pitches.length; i++) {
          this.playTone({
            type: 'square',
            freqStart: pitches[i],
            freqEnd: pitches[i] * 0.99,
            freqRampEnd: 0.03,
            gainStart: 0.12,
            gainEnd: 0.001,
            gainRampEnd: 0.03,
            startTime: now + i * 0.025,
          });
        }
        break;
      }
      default:
        // Other cards have no dedicated impact sound.
        break;
    }
  }

  setMasterVolume(vol: number): void {
    const v = clamp(vol, 0, 1);
    Howler.volume(v);
    if (this.masterGain) {
      this.masterGain.gain.value = v;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.ambientOsc) {
      this.ambientOsc.stop();
      this.ambientOsc.disconnect();
      this.ambientOsc = null;
    }
    this.stopBgmLoop();
    this.setLowHpHeartbeat(false);
    if (this.bgmGain) {
      this.bgmGain.disconnect();
      this.bgmGain = null;
    }
    if (this.masterGain) {
      this.masterGain.disconnect();
      this.masterGain = null;
    }

    Howler.unload();
    this.ctx = null;
    this.noiseBuffer = null;
  }
}
